import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { WebhookKey, WebhookKeyStatus, WebhookKeyWithPlain } from "../../types";
import { webhookKeyService } from "../../services/webhookKeyService";
import WebhookKeyRevealModal from "./WebhookKeyRevealModal";

const STATUS_CHIP: Record<
  WebhookKeyStatus,
  { label: string; color: "success" | "warning" | "error" }
> = {
  active: { label: "Attiva", color: "success" },
  disabled: { label: "Disabilitata", color: "warning" },
  revoked: { label: "Revocata", color: "error" },
};

type StatusFilter = WebhookKeyStatus | "all";

export default function WebhookKeysList() {
  const navigate = useNavigate();

  const [keys, setKeys] = useState<WebhookKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("all");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuKey, setMenuKey] = useState<WebhookKey | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<WebhookKey | null>(null);
  const [rotateTarget, setRotateTarget] = useState<WebhookKey | null>(null);
  const [rotateDisablePrevious, setRotateDisablePrevious] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [freshKey, setFreshKey] = useState<WebhookKeyWithPlain | null>(null);
  const [revealContext, setRevealContext] = useState<"created" | "rotated">("rotated");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await webhookKeyService.list();
      setKeys(res.data?.data?.keys ?? []);
    } catch (e) {
      console.error(e);
      setError("Impossibile caricare le webhook keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return keys.filter((k) => {
      if (filterStatus !== "all" && k.status !== filterStatus) return false;
      if (!q) return true;
      return (
        k.partnerName.toLowerCase().includes(q) ||
        k.partnerId.toLowerCase().includes(q) ||
        k.keyPrefix.toLowerCase().includes(q)
      );
    });
  }, [keys, filterText, filterStatus]);

  const openMenu = (e: React.MouseEvent<HTMLElement>, key: WebhookKey) => {
    setAnchorEl(e.currentTarget);
    setMenuKey(key);
  };
  const closeMenu = () => {
    setAnchorEl(null);
    setMenuKey(null);
  };

  const handleToggleStatus = async (key: WebhookKey) => {
    if (key.status === "revoked") return;
    const next: "active" | "disabled" = key.status === "active" ? "disabled" : "active";
    setTogglingId(key.id);
    setError(null);
    try {
      const res = await webhookKeyService.update(key.id, { status: next });
      const updated = res.data.data;
      setKeys((prev) => prev.map((k) => (k.id === key.id ? updated : k)));
    } catch (e) {
      console.error(e);
      setError("Errore nell'aggiornamento dello stato");
    } finally {
      setTogglingId(null);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await webhookKeyService.revoke(revokeTarget.id);
      const updated = res.data.data;
      setKeys((prev) => prev.map((k) => (k.id === revokeTarget.id ? updated : k)));
      setRevokeTarget(null);
    } catch (e) {
      console.error(e);
      setActionError("Errore durante la revoca");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRotateConfirm = async () => {
    if (!rotateTarget) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await webhookKeyService.rotate(rotateTarget.id, {
        disablePrevious: rotateDisablePrevious,
      });
      const newKey = res.data.data;
      setRevealContext("rotated");
      setFreshKey(newKey);
      setRotateTarget(null);
      // refresh list per riflettere lo stato della vecchia + nuova
      await load();
    } catch (e) {
      console.error(e);
      setActionError("Errore durante la rotazione");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Webhook Keys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestione chiavi multi-partner per autenticazione webhook EGG.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/egg/webhook-keys/new")}
        >
          Nuova chiave
        </Button>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Cerca per partner..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e: SelectChangeEvent<StatusFilter>) =>
              setFilterStatus(e.target.value as StatusFilter)
            }
          >
            <MenuItem value="all">Tutti</MenuItem>
            <MenuItem value="active">Attive</MenuItem>
            <MenuItem value="disabled">Disabilitate</MenuItem>
            <MenuItem value="revoked">Revocate</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Partner</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell width={200}>Creata</TableCell>
                <TableCell width={200}>Ultimo utilizzo</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      {keys.length === 0
                        ? "Nessuna chiave configurata"
                        : "Nessuna chiave trovata"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((key) => {
                  const status = STATUS_CHIP[key.status];
                  return (
                    <TableRow
                      key={key.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/egg/webhook-keys/${key.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {key.partnerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {key.partnerId} · {key.keyPrefix}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          size="small"
                          color={status.color}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {new Date(key.createdAt).toLocaleString("it-IT")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          by {key.createdBy}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {key.lastUsedAt ? (
                          <>
                            <Typography variant="caption" fontFamily="monospace">
                              {new Date(key.lastUsedAt).toLocaleString("it-IT")}
                            </Typography>
                            {key.lastUsedIp && (
                              <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace">
                                {key.lastUsedIp}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            Mai utilizzata
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          disabled={togglingId === key.id}
                          onClick={(e) => openMenu(e, key)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuKey) navigate(`/egg/webhook-keys/${menuKey.id}`);
            closeMenu();
          }}
        >
          Modifica
        </MenuItem>
        {menuKey && menuKey.status !== "revoked" && (
          <MenuItem
            onClick={() => {
              if (menuKey) handleToggleStatus(menuKey);
              closeMenu();
            }}
          >
            {menuKey.status === "active" ? "Disabilita" : "Riattiva"}
          </MenuItem>
        )}
        {menuKey && menuKey.status !== "revoked" && (
          <MenuItem
            onClick={() => {
              setRotateTarget(menuKey);
              setRotateDisablePrevious(true);
              setActionError(null);
              closeMenu();
            }}
          >
            Ruota
          </MenuItem>
        )}
        {menuKey && menuKey.status !== "revoked" && (
          <MenuItem
            onClick={() => {
              setRevokeTarget(menuKey);
              setActionError(null);
              closeMenu();
            }}
            sx={{ color: "error.main" }}
          >
            Revoca
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={!!revokeTarget}
        onClose={() => !actionLoading && setRevokeTarget(null)}
      >
        <DialogTitle>Revoca chiave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stai per revocare la chiave del partner{" "}
            <strong>{revokeTarget?.partnerName}</strong>. Operazione{" "}
            <strong>irreversibile</strong>: tutte le richieste in arrivo con
            questa chiave verranno rifiutate. La propagazione cross-instance
            può richiedere fino a 60 secondi.
          </DialogContentText>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRevokeTarget(null)}
            disabled={actionLoading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? "Revoca in corso..." : "Revoca"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rotateTarget}
        onClose={() => !actionLoading && setRotateTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ruota chiave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Verrà generata una nuova chiave per il partner{" "}
            <strong>{rotateTarget?.partnerName}</strong>. Il plaintext della
            nuova chiave sarà visibile <strong>una sola volta</strong>.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={rotateDisablePrevious}
                onChange={(e) => setRotateDisablePrevious(e.target.checked)}
              />
            }
            label="Disabilita immediatamente la chiave attuale (consigliato)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
            Se deselezionato, la vecchia chiave resterà valida finché non la
            disabiliti manualmente — utile per gestire la transizione lato
            partner senza downtime.
          </Typography>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRotateTarget(null)}
            disabled={actionLoading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleRotateConfirm}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? "Rotazione..." : "Ruota chiave"}
          </Button>
        </DialogActions>
      </Dialog>

      <WebhookKeyRevealModal
        keyData={freshKey}
        context={revealContext}
        onClose={() => setFreshKey(null)}
      />
    </Box>
  );
}
